import { Component } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { withTranslation } from '../../hoc/withTranslation';
import Footer from '../Sections/Footer';
import TrustMarker from '../common/TrustMarker';
import { getEntityThemedCtaButtonSx } from '../../utils/ctaContrast.utils';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';
import { postMessage } from '../../utils/common.utils';
import { captureMfaPinRecoveryStarted } from '../../utils/analytics/posthog.utils';

class PinRecoveryScreen extends Component {
	constructor(props) {
		super(props);
		this.state = {
			headerHeight: 70,
		};
	}

	calculateHeaderHeight = () => {
		const appBar = document.querySelector('header.MuiAppBar-root');
		if (!appBar) return;
		const height = Math.ceil(appBar.getBoundingClientRect().height);
		if (height && height !== this.state.headerHeight) {
			this.setState({ headerHeight: height });
		}
	};

	componentDidMount() {
		this.calculateHeaderHeight();
		window.addEventListener('resize', this.calculateHeaderHeight);
		requestAnimationFrame(() => this.calculateHeaderHeight());
		captureMfaPinRecoveryStarted({
			source: 'pin_recovery_screen',
			hasBiometric: Boolean(this.props.hasBiometric),
		});
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.calculateHeaderHeight);
	}

	handleContactSupport = () => {
		const handleId =
			this.props.handleId || global.handleId || global.handleID || '';
		window.location.href = `mailto:support@finvu.in?subject=${encodeURIComponent(
			handleId ? `PIN Locked - ${handleId}` : 'PIN Locked',
		)}`;
	};

	handleExitJourney = () => {
		postMessage('exit', 'BACK');
		if (global.isRedirect && global.redirectUrl?.includes('?')) {
			window.location.replace(
				`${global.redirectUrl}&ecres=${global.encryptedres}&resdate=${global.resDate}&fi=${global.encryptedFiId}`,
			);
		} else if (global.isRedirect) {
			window.location.replace(
				`${global.redirectUrl}?${global.redirectUrl.includes('?') ? '&' : ''}ecres=${global.encryptedres}&resdate=${global.resDate}&fi=${global.encryptedFiId}`,
			);
		} else if (this.props.onClose) {
			this.props.onClose();
		}
	};

	render() {
		const {
			t,
			customDetails,
			onRetryBiometric,
			hasBiometric = true,
		} = this.props;
		const isDark = customDetails?.darkTheme === true;
		const primaryColor = customDetails?.primaryColor || '#273C89';
		const entityName = global.entityName || 'Credright Finance';
		const handleId =
			this.props.handleId || global.handleId || global.handleID || '';

		const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
		const isAndroid = /android/i.test(ua);
		const isIOS = /iphone|ipad|ipod/i.test(ua);
		const biometricLabel = isAndroid
			? 'Fingerprint'
			: isIOS
				? 'Face ID'
				: 'Passkey';

		const subtitleText = hasBiometric
			? `You’ve used all 3 PIN attempts. Try ${biometricLabel} again, or contact support to regain access.`
			: 'You’ve used all 3 PIN attempts. Contact support to regain access.';

		const primaryCtaSx = getEntityThemedCtaButtonSx(customDetails, isDark, {
			py: 1.5,
			fontSize: '0.9375rem',
			fontWeight: 600,
			borderRadius: customDetails?.borderRadius || '8px',
			textTransform: 'none',
			boxShadow: 'none',
			...keyboardFocusVisibleSx,
		});

		const secondaryCtaBg = isDark ? 'rgba(255,255,255,0.08)' : '#E8EEF9';
		const secondaryCtaColor = isDark ? '#90CAF9' : primaryColor;

		return (
			<Box
				sx={{
					minHeight: '100vh',
					display: 'flex',
					flexDirection: 'column',
					backgroundColor: isDark ? '#121212' : '#FFFFFF',
					color: isDark ? '#FFFFFF' : '#111111',
					fontFamily: customDetails?.fontFamily,
					boxSizing: 'border-box',
					pt: `${this.state.headerHeight}px`,
				}}
			>
				{/* TrustMarker Sub-banner directly under global fixed Header */}
				<TrustMarker customDetails={customDetails} noBorderRadius />

				{/* Main Content Body */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						px: 3,
						py: 2,
						maxWidth: '440px',
						mx: 'auto',
						width: '100%',
						boxSizing: 'border-box',
					}}
				>
					{/* Orange Warning Icon */}
					<Box
						sx={{
							width: '64px',
							height: '64px',
							borderRadius: '50%',
							backgroundColor: '#D94814',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							mb: 2.5,
							boxShadow: '0 4px 12px rgba(217, 72, 20, 0.25)',
						}}
					>
						<PriorityHighIcon sx={{ fontSize: 38, color: '#FFFFFF' }} />
					</Box>

					{/* Main Title */}
					<Typography
						component="h1"
						sx={{
							fontSize: '1.25rem',
							fontWeight: 700,
							textAlign: 'center',
							mb: 1.25,
							color: isDark ? '#FFFFFF' : '#111111',
						}}
					>
						{t('pinSetup.lockedTitle') || 'Too many incorrect PIN attempts'}
					</Typography>

					{/* Subtitle */}
					<Typography
						sx={{
							fontSize: '0.875rem',
							lineHeight: 1.5,
							textAlign: 'center',
							color: isDark ? '#B0B0B0' : '#666666',
							mb: 3,
							maxWidth: '320px',
						}}
					>
						{subtitleText}
					</Typography>

					{/* Support Details Box */}
					<Box
						sx={{
							width: '100%',
							borderRadius: '12px',
							border: `1px solid ${isDark ? '#333333' : '#E5E7EB'}`,
							backgroundColor: isDark ? '#1A1A1A' : '#FAFAFA',
							p: 2,
							mb: 3,
							boxSizing: 'border-box',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								mb: 1.25,
							}}
						>
							<Typography
								sx={{
									fontSize: '0.8125rem',
									color: isDark ? '#9AA3A8' : '#71717A',
								}}
							>
								Email
							</Typography>
							<Typography
								sx={{
									fontSize: '0.875rem',
									fontWeight: 600,
									color: isDark ? '#FFFFFF' : '#18181B',
								}}
							>
								support@finvu.in
							</Typography>
						</Box>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
							}}
						>
							<Typography
								sx={{
									fontSize: '0.8125rem',
									color: isDark ? '#9AA3A8' : '#71717A',
								}}
							>
								Handle
							</Typography>
							<Typography
								sx={{
									fontSize: '0.875rem',
									fontWeight: 600,
									color: isDark ? '#FFFFFF' : '#18181B',
								}}
							>
								{handleId}
							</Typography>
						</Box>
					</Box>

					{/* Action Buttons */}
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: 1.5,
						}}
					>
						{/* 1. Contact Support */}
						<Button
							variant="contained"
							fullWidth
							onClick={this.handleContactSupport}
							sx={primaryCtaSx}
						>
							Contact support
						</Button>

						{/* 2. Try Biometric Again */}
						{hasBiometric && onRetryBiometric && (
							<Button
								variant="contained"
								fullWidth
								onClick={onRetryBiometric}
								sx={{
									py: 1.5,
									fontSize: '0.9375rem',
									fontWeight: 600,
									borderRadius: customDetails?.borderRadius || '8px',
									backgroundColor: secondaryCtaBg,
									color: secondaryCtaColor,
									textTransform: 'none',
									boxShadow: 'none',
									'&:hover': {
										backgroundColor: secondaryCtaBg,
										boxShadow: 'none',
									},
									...keyboardFocusVisibleSx,
								}}
							>
								{`Try ${biometricLabel} Again`}
							</Button>
						)}

						{/* 3. Back to Entity */}
						<Button
							variant="outlined"
							fullWidth
							onClick={this.handleExitJourney}
							sx={{
								py: 1.5,
								fontSize: '0.9375rem',
								fontWeight: 600,
								borderRadius: customDetails?.borderRadius || '8px',
								borderColor: isDark ? '#444444' : '#D1D5DB',
								color: isDark ? '#FFFFFF' : '#374151',
								textTransform: 'none',
								'&:hover': {
									borderColor: isDark ? '#666666' : '#9CA3AF',
									backgroundColor: 'transparent',
								},
								...keyboardFocusVisibleSx,
							}}
						>
							{`Back to ${entityName}`}
						</Button>
					</Box>
				</Box>

				{/* Secure Footer */}
				<Footer customDetails={customDetails} />
			</Box>
		);
	}
}

export default withTranslation(PinRecoveryScreen);
